import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Loader2, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ORDER_STATUS_FLOW, ORDER_STATUS_LABELS, orderStatusTone } from "@/lib/constants";
import { formatDate } from "@/lib/format";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const canCreate = !isMobile && hasRole(user, "admin", "sales", "order_manager");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [supplierId, setSupplierId] = useState(searchParams.get("supplier_id") || "");
  const [productCategoryId, setProductCategoryId] = useState(searchParams.get("product_category_id") || "");
  const [onlyDelayed, setOnlyDelayed] = useState(searchParams.get("only_delayed") === "true");

  const [suppliers, setSuppliers] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setQ(searchParams.get("q") || "");
    setStatus(searchParams.get("status") || "");
    setSupplierId(searchParams.get("supplier_id") || "");
    setProductCategoryId(searchParams.get("product_category_id") || "");
    setOnlyDelayed(searchParams.get("only_delayed") === "true");
  }, [searchParams]);

  const activeFilterCount = [
    searchParams.get("q"),
    searchParams.get("status"),
    searchParams.get("supplier_id"),
    searchParams.get("product_category_id"),
    searchParams.get("only_delayed") === "true" ? "delayed" : "",
  ].filter(Boolean).length;

  const applyFiltersToUrl = (overrides = {}) => {
    const nextQ = overrides.q !== undefined ? overrides.q : q;
    const nextStatus = overrides.status !== undefined ? overrides.status : status;
    const nextSupplierId = overrides.supplierId !== undefined ? overrides.supplierId : supplierId;
    const nextProductCategoryId = overrides.productCategoryId !== undefined ? overrides.productCategoryId : productCategoryId;
    const nextOnlyDelayed = overrides.onlyDelayed !== undefined ? overrides.onlyDelayed : onlyDelayed;
    const nextPage = overrides.page !== undefined ? overrides.page : 1;

    const p = new URLSearchParams();
    if (nextQ) p.set("q", nextQ);
    if (nextStatus) p.set("status", nextStatus);
    if (nextSupplierId) p.set("supplier_id", nextSupplierId);
    if (nextProductCategoryId) p.set("product_category_id", nextProductCategoryId);
    if (nextOnlyDelayed) p.set("only_delayed", "true");
    if (nextPage > 1) p.set("page", String(nextPage));

    setSearchParams(p);
  };

  const clearFilters = () => {
    setQ("");
    setStatus("");
    setSupplierId("");
    setProductCategoryId("");
    setOnlyDelayed(false);
    setSearchParams({});
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      for (const [key, val] of searchParams.entries()) {
        params[key] = val;
      }
      if (!params.page) params.page = 1;
      if (!params.page_size) params.page_size = 25;

      const r = await api.get("/orders", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadRefs = async () => {
    const [s, p] = await Promise.all([api.get("/suppliers"), api.get("/product-categories")]);
    setSuppliers(s.data);
    setProductCategories(p.data);
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">Siparişler</h1>
          <p className="text-xs text-slate-500 mt-1">Tedarikçi siparişleri ve teslim takibi.</p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate("/siparisler/yeni")} className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm" data-testid="orders-create-button">
            <Plus size={16} className="mr-2" /> Yeni Sipariş
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 shadow-sm">
        {/* Search bar & mobile filter trigger button */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Sipariş no, iş no, müşteri..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 border-[#DCE2E8] focus-visible:ring-[#FF353A] text-base md:text-sm h-11 md:h-9"
              data-testid="orders-search-input"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setMobileFilterOpen((v) => !v)}
            className="md:hidden border-[#DCE2E8] min-h-[44px] min-w-[44px] px-3 relative flex items-center gap-2"
            data-testid="orders-mobile-filter-trigger"
          >
            <Filter size={18} />
            <span className="text-xs font-medium">Filtreler</span>
            {activeFilterCount > 0 && (
              <span className="h-5 w-5 rounded-full bg-[#FF353A] text-white text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {/* Filters Container */}
        <form
          onSubmit={(e) => { e.preventDefault(); applyFiltersToUrl({ page: 1 }); }}
          className={`mt-3 grid gap-3 md:grid-cols-6 ${mobileFilterOpen ? "block" : "hidden md:grid"}`}
        >
          <Select value={status || "__all__"} onValueChange={(v) => { const next = v === "__all__" ? "" : v; setStatus(next); applyFiltersToUrl({ status: next, page: 1 }); }}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="orders-status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__all__">Tüm Durumlar</SelectItem>
              {ORDER_STATUS_FLOW.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
            </SelectContent>
          </Select>

          <Select value={supplierId || "__all__"} onValueChange={(v) => { const next = v === "__all__" ? "" : v; setSupplierId(next); applyFiltersToUrl({ supplierId: next, page: 1 }); }}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="orders-supplier-filter"><SelectValue placeholder="Tedarikçi" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__all__">Tüm Tedarikçiler</SelectItem>
              {suppliers.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>

          <Select value={productCategoryId || "__all__"} onValueChange={(v) => { const next = v === "__all__" ? "" : v; setProductCategoryId(next); applyFiltersToUrl({ productCategoryId: next, page: 1 }); }}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="orders-product-filter"><SelectValue placeholder="Ürün Grubu" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__all__">Tüm Ürünler</SelectItem>
              {productCategories.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>

          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer py-2">
            <input
              type="checkbox"
              checked={onlyDelayed}
              onChange={(e) => { setOnlyDelayed(e.target.checked); applyFiltersToUrl({ onlyDelayed: e.target.checked, page: 1 }); }}
              data-testid="orders-only-delayed"
              className="h-4 w-4 rounded border-slate-300 text-[#FF353A] focus:ring-[#FF353A]"
            />
            Sadece Gecikmiş
          </label>

          <div className="md:col-span-6 flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100 md:border-t-0">
            <Button
              type="submit"
              variant="secondary"
              className="bg-[#F4F6F8] hover:bg-slate-200 text-[#171A1F] border border-[#DCE2E8] min-h-[44px] md:min-h-0 min-w-[80px]"
              data-testid="orders-filter-apply"
            >
              Uygula
            </Button>
            {activeFilterCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-800 text-xs min-h-[44px] md:min-h-0"
                data-testid="orders-filter-clear"
              >
                <X size={14} className="mr-1" /> Temizle
              </Button>
            )}
            <span className="ml-auto text-xs text-slate-500 py-1">Toplam <span className="font-semibold text-[#171A1F]">{total.toLocaleString("tr-TR")}</span> kayıt</span>
          </div>
        </form>
      </div>

      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500" data-testid="orders-loading">
            <Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...
          </div>
        ) : items.length === 0 ? (
          <EmptyState testId="orders-empty" title="Sipariş bulunamadı" description="Filtreleri değiştirin veya yeni sipariş oluşturun." />
        ) : (
          <>
            {/* Mobile Card List View */}
            <div className="block md:hidden divide-y divide-[#DCE2E8]">
              {items.map((o) => (
                <div
                  key={o.id}
                  onClick={() => navigate(`/siparisler/${o.id}`)}
                  className="p-4 space-y-2 cursor-pointer hover:bg-[#EAF7FF]/20 active:bg-[#EAF7FF]/50 transition-colors"
                  data-testid={`order-card-${o.order_number}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-[#FF353A]">{o.order_number}</span>
                      <span className="font-mono text-xs text-slate-500">({o.job_number})</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge tone={orderStatusTone(o.status)}>{ORDER_STATUS_LABELS[o.status]}</StatusBadge>
                      {o.is_delayed && (
                        <StatusBadge tone="danger" testId={`order-delayed-mobile-${o.order_number}`}>Gecikmiş</StatusBadge>
                      )}
                    </div>
                  </div>

                  <div className="text-sm font-semibold text-[#171A1F]">{o.customer_name}</div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 pt-1 border-t border-slate-100">
                    <div>
                      <span className="text-slate-400 block text-[10px]">Tedarikçi</span>
                      <span className="truncate font-medium block">{o.supplier_name || "-"}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Ürün Grubu</span>
                      <span className="truncate font-medium block">{o.product_category_name || "-"}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Sipariş Tarihi</span>
                      <span>{formatDate(o.order_date)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Tahmini Teslim</span>
                      <span>{formatDate(o.estimated_delivery_date)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sipariş No</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İş No</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Müşteri</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tedarikçi</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ürün Grubu</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sipariş Tarihi</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tahmini Teslim</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Durum</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sorumlu</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Gecikme</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((o) => (
                  <TableRow key={o.id} className="gp-row cursor-pointer border-b border-[#DCE2E8]" onClick={() => navigate(`/siparisler/${o.id}`)} data-testid={`order-row-${o.order_number}`}>
                    <TableCell className="font-mono text-sm font-semibold text-[#FF353A]">{o.order_number}</TableCell>
                    <TableCell className="font-mono text-sm text-[#171A1F]">{o.job_number}</TableCell>
                    <TableCell className="text-sm text-[#171A1F]">{o.customer_name}</TableCell>
                    <TableCell className="text-sm text-slate-700">{o.supplier_name || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-700">{o.product_category_name || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(o.order_date)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(o.estimated_delivery_date)}</TableCell>
                    <TableCell><StatusBadge tone={orderStatusTone(o.status)}>{ORDER_STATUS_LABELS[o.status]}</StatusBadge></TableCell>
                    <TableCell className="text-sm text-slate-700">{o.responsible_user_name || "-"}</TableCell>
                    <TableCell>{o.is_delayed ? <StatusBadge tone="danger" testId={`order-delayed-${o.order_number}`}>Gecikmiş</StatusBadge> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {!loading && items.length > 0 && (
          <Pagination
            page={parseInt(searchParams.get("page") || "1", 10)}
            pageSize={parseInt(searchParams.get("page_size") || "25", 10)}
            total={total}
            onPageChange={(p) => applyFiltersToUrl({ page: p })}
            onPageSizeChange={(s) => {
              const p = new URLSearchParams(searchParams);
              p.set("page_size", String(s));
              p.set("page", "1");
              setSearchParams(p);
            }}
          />
        )}
      </div>
    </div>
  );
}
